import { useEffect, useState } from 'react';

import {
  newShareAccessRow,
  setPermission,
  SharePermissionField,
  toShareAccessRow,
} from './shareAccessHelpers';
import { ReportAccessData, ReportAccessResponse, SavedReportsList } from '../../../../../@types';
import {
  useAddNewSavedReportAccess,
  useUpdateReportAccessDataById,
} from '../../../../../queries/report-query';
import { toast } from '../../../../../utils/toast';

/**
 * Row state, the search-selection handshake and the save/remove mutations for the
 * Direct Share tab, so the component itself is only chrome — the same split as
 * `useScheduleEmailForm` and `useReportSettingsForm` on the other two tabs.
 */
export const useShareAccessForm = (
  savedData: ReportAccessResponse | undefined,
  handleRefetch: (refetch: boolean) => void,
  rowData?: SavedReportsList
) => {
  const [searchData, setSearchData] = useState<string>();
  const [searchDataId, setSearchDataId] = useState<number>();
  const [tableRowData, setTableRowData] = useState<ReportAccessData[]>([]);
  /** Held as the message itself: saving and removing fail in different ways. */
  const [pendingRemoval, setPendingRemoval] = useState<ReportAccessData>();

  const addNewSavedReportAccess = useAddNewSavedReportAccess();
  const updateTemplateQuery = useUpdateReportAccessDataById();

  useEffect(() => {
    savedData && setTableRowData(savedData.data.map(toShareAccessRow));
  }, [savedData]);

  useEffect(() => {
    if (searchData && searchDataId) {
      const rowExist = tableRowData?.some(
        (item: ReportAccessData) => item.entityId === searchDataId
      );
      rowExist
        ? toast.warning('Search item is already in the list')
        : setTableRowData([...tableRowData, newShareAccessRow(searchDataId, searchData)]);

      setSearchData('');
      setSearchDataId(0);
    }
    // Runs on a completed search selection only; `tableRowData` is read here but
    // listing it would re-run this on every permission toggle.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchData, searchDataId]);

  const handleSave = async () => {
    // Checked up front rather than inside the loop: without a report to attach
    // them to, newly added recipients cannot be saved at all, and a mid-loop
    // failure would report "some changes may not have been saved" when in fact
    // nothing could be. Falling through silently dropped them and then reported
    // success, which was worse than either.
    const reportId = rowData?.id;
    if (reportId === undefined && tableRowData.some((data) => !data.id)) {
      toast.error('Sharing not saved', {
        description: 'This report cannot be shared until it has been saved.',
      });
      return;
    }

    try {
      for (const data of tableRowData) {
        if (data.id) {
          await updateTemplateQuery.mutateAsync({ id: data.id, data });
        } else if (reportId !== undefined) {
          // Re-checked rather than trusted: the guard above already returns on a
          // missing id, but only this narrows it for the payload.
          const response = await addNewSavedReportAccess.mutateAsync({ reportId, data });
          setTableRowData((rows) =>
            rows.map((row) =>
              row.entityId === response.entityId ? toShareAccessRow(response) : row
            )
          );
        }
      }
    } catch {
      // The footer calls this straight from `onClick`. Without this the rejection
      // went nowhere: no toast, no refetch, and a dialog that looked saved. The
      // loop is sequential, so rows before the failure are already persisted —
      // hence "some changes", and no state reset.
      toast.error('Sharing not saved', {
        description: 'Some changes may not have been saved. Please try again.',
      });
      return;
    }

    handleRefetch(true);
    setSearchData('');
    setSearchDataId(0);
  };

  const handleConfirmRemoval = async () => {
    const target = pendingRemoval;
    setPendingRemoval(undefined);
    if (!target) return;

    if (target.id) {
      try {
        await updateTemplateQuery.mutateAsync({
          id: target.id,
          data: {
            canCustomizeColumns: target.canCustomizeColumns,
            canCustomizeSearch: target.canCustomizeSearch,
            canEdit: target.canEdit,
            entityId: target.entityId,
            isActive: false,
          },
        });
      } catch {
        // Dropping the row here regardless would have told the user their access
        // was revoked while the server still had it — the worst of the two lies.
        toast.error('Sharing not saved', {
          description: `${target.name || 'This recipient'} still has access. Please try again.`,
        });
        return;
      }
    }
    setTableRowData((rows) => rows.filter((row) => row.entityId !== target.entityId));
    handleRefetch(true);
  };

  return {
    searchData,
    onPickSearchResult: (id: number, name: string) => {
      setSearchData(name);
      setSearchDataId(id);
    },
    onClearSearch: () => {
      setSearchData('');
      setSearchDataId(undefined);
    },
    tableRowData,
    onTogglePermission: (
      entityId: number | undefined,
      field: SharePermissionField,
      checked: boolean
    ) => setTableRowData((rows) => setPermission(rows, entityId, field, checked)),
    pendingRemoval,
    requestRemoval: setPendingRemoval,
    cancelRemoval: () => setPendingRemoval(undefined),
    handleConfirmRemoval,
    handleSave,
    isSaving: updateTemplateQuery.isPending || addNewSavedReportAccess.isPending,
  };
};
